export function bucketInitial(name: string | null | undefined): string {
  const initial = name?.trim().match(/[A-Za-z0-9]/)?.[0];
  return initial?.toUpperCase() ?? 'C';
}

/**
 * Copies text from the hosted sidebar. The Clipboard API needs the host page
 * to delegate clipboard-write to this iframe; the selection-copy fallback
 * covers browsers or embeddings where that delegation is missing.
 */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const scratch = document.createElement('textarea');
    scratch.value = text;
    scratch.setAttribute('readonly', '');
    scratch.style.position = 'fixed';
    scratch.style.opacity = '0';
    document.body.append(scratch);
    scratch.select();
    let copied = false;
    try {
      copied = document.execCommand('copy');
    } catch {
      copied = false;
    }
    scratch.remove();
    return copied;
  }
}

export function formatRelativeTime(timestamp: string, now = Date.now()): string {
  const elapsed = Math.max(0, now - Date.parse(timestamp));
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

export function createOperationId(): string {
  return crypto.randomUUID();
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return count === 1 ? singular : plural;
}

/** Caps a string at max characters, counting code points like bucketNameAt. */
export function truncateChars(value: string, max: number): string {
  return Array.from(value).slice(0, max).join('');
}
